import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import appointmentService, { AppointmentCompletionDto, AppointmentFilters, CreateAppointmentDto, DayStats } from '../../services/appointmentService';
import { Appointment } from '../../types';
import { formatDateValue } from '../../utils/dateValue';

const errorMessage = (e: unknown): string =>
  (e as AxiosError<{ message?: string }>).response?.data?.message ?? (e as Error).message;

interface AppointmentState {
  list:        Appointment[];
  selected:    Appointment | null;
  activeDate:  string;
  slots:       string[];
  stats:       DayStats | null;
  loading:     boolean;
  error:       string | null;
}

const initialState: AppointmentState = {
  list: [], selected: null,
  activeDate: formatDateValue(new Date()),
  slots: [], stats: null, loading: false, error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────
export const fetchAppointments = createAsyncThunk(
  'appointments/fetchAll',
  async (filters: AppointmentFilters, { rejectWithValue }) => {
    try { return await appointmentService.getAll(filters); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const createAppointment = createAsyncThunk(
  'appointments/create',
  async (data: CreateAppointmentDto, { rejectWithValue }) => {
    try { return await appointmentService.create(data); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const updateAppointment = createAsyncThunk(
  'appointments/update',
  async ({ id, data }: { id: string; data: Partial<CreateAppointmentDto> }, { rejectWithValue }) => {
    try { return await appointmentService.update(id, data); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const updateAppointmentStatus = createAsyncThunk(
  'appointments/updateStatus',
  async ({ id, status, completion }: { id: string; status: string; completion?: AppointmentCompletionDto }, { rejectWithValue }) => {
    try { return await appointmentService.updateStatus(id, status, completion); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const cancelAppointment = createAsyncThunk(
  'appointments/cancel',
  async ({ id, reason }: { id: string; reason?: string }, { rejectWithValue }) => {
    try { return await appointmentService.cancel(id, reason); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const rescheduleAppointment = createAsyncThunk(
  'appointments/reschedule',
  async ({ id, date, startTime }: { id: string; date: string; startTime: string }, { rejectWithValue }) => {
    try { return await appointmentService.reschedule(id, date, startTime); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const fetchDayStats = createAsyncThunk(
  'appointments/fetchStats',
  async (date: string, { rejectWithValue }) => {
    try { return await appointmentService.getStats(date); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

export const fetchAvailableSlots = createAsyncThunk(
  'appointments/fetchSlots',
  async ({ date, stylist,duration }: { date: string; stylist?: string;duration?:number }, { rejectWithValue }) => {
    try { return await appointmentService.getAvailableSlots(date, stylist,duration); }
    catch (e: unknown) { return rejectWithValue(errorMessage(e)); }
  },
);

const upsert = (list: Appointment[], appt: Appointment) => {
  const i = list.findIndex(x => x._id === appt._id);
  if (i !== -1) list[i] = appt; else list.unshift(appt);
};

const appointmentSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    setActiveDate: (state, action: PayloadAction<string>) => { state.activeDate = action.payload; },
    setSelected:   (state, action: PayloadAction<Appointment | null>) => { state.selected = action.payload; },
    syncAppointment: (state, action: PayloadAction<Appointment>) => {
      upsert(state.list, action.payload);
      if (state.selected?._id === action.payload._id) state.selected = action.payload;
    },
    clearError:    (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending,    (s) => { s.loading = true;  s.error = null; })
      .addCase(fetchAppointments.fulfilled,  (s, a) => { s.loading = false; s.list = a.payload.data ?? []; })
      .addCase(fetchAppointments.rejected,   (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createAppointment.fulfilled,  (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(createAppointment.rejected,   (s, a) => { s.error = a.payload as string; })
      .addCase(updateAppointment.fulfilled,  (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } })
      .addCase(updateAppointment.rejected,   (s, a) => { s.error = a.payload as string; })
      .addCase(updateAppointmentStatus.fulfilled, (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(cancelAppointment.fulfilled,       (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(rescheduleAppointment.fulfilled,   (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(rescheduleAppointment.rejected,    (s, a) => { s.error = a.payload as string; })
      .addCase(fetchDayStats.fulfilled,  (s, a) => { s.stats = a.payload.data ?? null; })
      .addCase(fetchAvailableSlots.fulfilled, (s, a) => { s.slots = a.payload.data ?? []; });
  },
});

export const { setActiveDate, setSelected, syncAppointment, clearError } = appointmentSlice.actions;
export default appointmentSlice.reducer;
